import React, { useEffect, useState } from "react";
import { axiosInstance } from "../../../Api/config";
import { MdOutlineComment, MdAdd, MdEdit, MdDelete, MdStar } from "react-icons/md";

const Testimonials = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingReview, setEditingReview] = useState(null);

  // Form fields
  const [form, setForm] = useState({
    name: "",
    location: "",
    bike: "",
    review: "",
    stars: 5,
    is_active: 1
  });
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [bikeModels, setBikeModels] = useState([]);

  const fetchReviews = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get("/testimonials");
      setReviews(response.data || []);
    } catch (err) {
      console.error("Error fetching reviews:", err);
      setError("Failed to fetch testimonials.");
    } finally {
      setLoading(false);
    }
  };

  const fetchModels = async () => {
    try {
      const res = await axiosInstance.get("/bike-models/active");
      if (res.data && res.data.length > 0) {
        setBikeModels(res.data.map(b => b.name));
      }
    } catch (err) {
      console.error("Error fetching bike models:", err);
    }
  };

  useEffect(() => {
    fetchReviews();
    fetchModels();
  }, []);


  const handleOpenAdd = () => {
    setEditingReview(null);
    setForm({
      name: "",
      location: "",
      bike: "",
      review: "",
      stars: 5,
      is_active: 1
    });
    setError("");
    setModalOpen(true);
  };

  const handleOpenEdit = (rev) => {
    setEditingReview(rev);
    setForm({
      name: rev.name || "",
      location: rev.location || "",
      bike: rev.bike || "",
      review: rev.review || "",
      stars: parseInt(rev.stars) || 5,
      is_active: parseInt(rev.is_active) ?? 1
    });
    setError("");
    setModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!form.name || !form.review || !form.location) {
      setError("Name, Location, and Review description are required.");
      return;
    }

    try {
      let res;
      if (editingReview) {
        res = await axiosInstance.post(`/testimonials/${editingReview.id}`, form);
      } else {
        res = await axiosInstance.post("/testimonials", form);
      }

      if (res.data.success) {
        setSuccessMsg(editingReview ? "Testimonial updated!" : "Testimonial added!");
        setModalOpen(false);
        fetchReviews();
        setTimeout(() => setSuccessMsg(""), 3000);
      } else {
        setError(res.data.message || "Operation failed.");
      }
    } catch (err) {
      console.error(err);
      setError("Failed to save testimonial details.");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this review?")) return;
    try {
      const res = await axiosInstance.delete(`/testimonials/${id}`);
      if (res.data.success) {
        setSuccessMsg("Testimonial deleted!");
        fetchReviews();
        setTimeout(() => setSuccessMsg(""), 3000);
      }
    } catch (err) {
      console.error(err);
      setError("Failed to delete review.");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <p className="text-sm font-semibold uppercase tracking-[0.2em] text-amber-600">Admin</p>
          <h1 className="mt-2 text-3xl font-bold text-slate-950">Happy Customers</h1>
        </div>
        <button
          onClick={handleOpenAdd}
          className="flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-slate-950 px-4 py-2.5 rounded-lg text-sm font-bold shadow-md transition-all"
        >
          <MdAdd className="text-lg" />
          Add Testimonial
        </button>
      </div>

      {successMsg && (
        <div className="bg-emerald-50 text-emerald-700 border border-emerald-200 px-4 py-3 rounded-lg text-sm font-semibold">
          {successMsg}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-amber-600" />
        </div>
      ) : reviews.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-8 text-center text-slate-500 shadow-sm">
          <MdOutlineComment className="text-5xl mx-auto text-slate-300 mb-3" />
          <p>No testimonials found. Click "Add Testimonial" to create one.</p>
        </div>
      ) : (
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {reviews.map((rev) => (
            <article key={rev.id} className="bg-white border border-slate-200 rounded-xl shadow-sm p-5 flex flex-col justify-between space-y-4">
              <div className="space-y-2">
                <div className="flex justify-between items-start">
                  <div className="flex items-center gap-1">
                    {[...Array(parseInt(rev.stars) || 5)].map((_, i) => (
                      <MdStar key={i} className="text-amber-500 text-lg" />
                    ))}
                  </div>
                  <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded ${
                    parseInt(rev.is_active) === 1 ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-500"
                  }`}>
                    {parseInt(rev.is_active) === 1 ? "Visible" : "Hidden"}
                  </span>
                </div>
                <p className="text-slate-600 text-sm leading-relaxed italic">"{rev.review}"</p>
              </div>

              <div className="border-t border-slate-100 pt-3 flex justify-between items-end">
                <div>
                  <h3 className="font-bold text-slate-900 leading-tight">{rev.name}</h3>
                  <p className="text-xs text-slate-400 mt-0.5">{rev.location} • {rev.bike || "Royal Enfield"}</p>
                </div>
                <div className="flex gap-1 shrink-0">
                  <button
                    onClick={() => handleOpenEdit(rev)}
                    className="p-1.5 text-slate-500 hover:text-indigo-650 hover:bg-slate-50 rounded border border-slate-200 transition"
                    title="Edit"
                  >
                    <MdEdit />
                  </button>
                  <button
                    onClick={() => handleDelete(rev.id)}
                    className="p-1.5 text-slate-500 hover:text-rose-600 hover:bg-rose-50 rounded border border-slate-200 transition"
                    title="Delete"
                  >
                    <MdDelete />
                  </button>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}

      {/* MODAL */}
      {modalOpen && (
        <div className="fixed inset-0 z-[100] bg-slate-950/50 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden border border-slate-100 animate-in fade-in zoom-in-95 duration-200">
            <div className="border-b border-slate-100 px-6 py-4 flex justify-between items-center bg-slate-50">
              <h2 className="text-lg font-bold text-slate-950">
                {editingReview ? "Edit Review Details" : "Add Customer Testimonial"}
              </h2>
              <button onClick={() => setModalOpen(false)} className="text-slate-400 hover:text-slate-600 text-xl">
                ✕
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {error && (
                <div className="bg-rose-50 text-rose-600 border border-rose-100 px-4 py-2.5 rounded-lg text-sm">
                  {error}
                </div>
              )}

              <label className="block space-y-1">
                <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Customer Name</span>
                <input
                  type="text"
                  required
                  placeholder="e.g. Ramesh Kumar"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-amber-500 focus:bg-white transition-all"
                />
              </label>

              <div className="grid grid-cols-2 gap-4">
                <label className="block space-y-1">
                  <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">City / Location</span>
                  <input
                    type="text"
                    required
                    placeholder="e.g. Katpadi"
                    value={form.location}
                    onChange={(e) => setForm({ ...form, location: e.target.value })}
                    className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-amber-500 focus:bg-white transition-all"
                  />
                </label>

                <label className="block space-y-1">
                  <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Purchased Bike</span>
                  <select
                    value={form.bike}
                    onChange={(e) => setForm({ ...form, bike: e.target.value })}
                    className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-amber-500 focus:bg-white transition-all"
                  >
                    <option value="">Select a bike model</option>
                    {bikeModels.map((model) => (
                      <option key={model} value={model}>
                        {model}
                      </option>
                    ))}
                    <option value="General Brand">General Brand</option>
                  </select>
                </label>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <label className="block space-y-1">
                  <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Rating Stars</span>
                  <select
                    value={form.stars}
                    onChange={(e) => setForm({ ...form, stars: parseInt(e.target.value) })}
                    className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-amber-500 focus:bg-white transition-all"
                  >
                    <option value={5}>5 Stars ★★★★★</option>
                    <option value={4}>4 Stars ★★★★</option>
                    <option value={3}>3 Stars ★★★</option>
                    <option value={2}>2 Stars ★★</option>
                    <option value={1}>1 Star ★</option>
                  </select>
                </label>

                <label className="block space-y-1">
                  <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Visibility Status</span>
                  <select
                    value={form.is_active}
                    onChange={(e) => setForm({ ...form, is_active: parseInt(e.target.value) })}
                    className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-amber-500 focus:bg-white transition-all"
                  >
                    <option value={1}>Visible (Active)</option>
                    <option value={0}>Hidden (Inactive)</option>
                  </select>
                </label>
              </div>

              <label className="block space-y-1">
                <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Review Feedback</span>
                <textarea
                  rows="4"
                  required
                  placeholder="Paste what the customer said about their buying experience..."
                  value={form.review}
                  onChange={(e) => setForm({ ...form, review: e.target.value })}
                  className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-amber-500 focus:bg-white transition-all resize-none"
                />
              </label>

              <div className="flex justify-end gap-3 pt-4 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setModalOpen(false)}
                  className="px-5 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-sm font-semibold transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2.5 bg-amber-500 hover:bg-amber-600 text-slate-950 rounded-lg text-sm font-bold shadow-md transition"
                >
                  {editingReview ? "Save Changes" : "Create Review"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Testimonials;
